import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import Spinner from "@/components/Spinner";
import { MissionEyebrow } from "@/components/Mission";

const SECTION_TITLES = {
  student_info: "Student information",
  school_selection: "School selection",
  dependency: "Dependency status",
  parent_info: "Parent information",
  parent_finances: "Parent financials",
  student_finances: "Student financials",
  sign_submit: "Sign and submit",
  what_next: "What happens next",
};

const STORAGE_KEY = "aw_fafsa_v1";

const loadState = () => {
  try { const r = localStorage.getItem(STORAGE_KEY); return r ? JSON.parse(r) : null; } catch { return null; }
};
const saveState = (s) => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch {} };
const clearState = () => { try { localStorage.removeItem(STORAGE_KEY); } catch {} };

export default function FafsaPage() {
  const [state, setState] = useState(() => loadState());
  const [lesson, setLesson] = useState(null);
  const [lessonLoading, setLessonLoading] = useState(false);
  const [activeKey, setActiveKey] = useState(null);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [askBusy, setAskBusy] = useState(false);

  useEffect(() => { if (state) saveState(state); }, [state]);

  const startNew = (onboarding, sections) => {
    const s = {
      onboarding,
      sections: sections.map((sec) => ({ key: sec.key, status: "not_started" })),
      started_at: new Date().toISOString(),
      completed_at: null,
    };
    setState(s);
    setLesson(null); setActiveKey(null); setAnswer(""); setQuestion("");
  };

  const reset = () => {
    if (!window.confirm("Reset your walkthrough? Progress in this browser will be cleared.")) return;
    clearState(); setState(null); setLesson(null); setActiveKey(null);
  };

  const startSection = async (key) => {
    setActiveKey(key); setLesson(null); setAnswer(""); setLessonLoading(true);
    try {
      const r = await api.post("/aw/fafsa/teach", { section_key: key, onboarding: state.onboarding });
      setLesson(r.data?.lesson || null);
      setState((prev) => prev && {
        ...prev,
        sections: prev.sections.map((s) => s.key === key && s.status === "not_started"
          ? { ...s, status: "in_progress" } : s),
      });
    } catch (e) {
      setLesson({ error: e?.response?.data?.detail || "Couldn't generate the lesson." });
    } finally { setLessonLoading(false); }
  };

  const completeSection = () => {
    if (!activeKey) return;
    setState((prev) => {
      if (!prev) return prev;
      const next = {
        ...prev,
        sections: prev.sections.map((s) => s.key === activeKey ? { ...s, status: "complete" } : s),
      };
      const remaining = next.sections.find((s) => s.status !== "complete");
      if (!remaining) next.completed_at = new Date().toISOString();
      return next;
    });
    // Auto-advance
    setTimeout(() => {
      const fresh = loadState();
      const remaining = fresh?.sections?.find((s) => s.status !== "complete");
      if (remaining) startSection(remaining.key);
      else { setLesson({ celebration: true }); setActiveKey(null); }
    }, 100);
  };

  const ask = async (e) => {
    e?.preventDefault?.();
    if (!activeKey || !question.trim()) return;
    setAskBusy(true); setAnswer("");
    try {
      const r = await api.post("/aw/fafsa/ask", {
        section_key: activeKey, onboarding: state.onboarding, question,
      });
      setAnswer(r.data?.answer || "");
    } catch { setAnswer("Couldn't answer right now. Try again in a moment."); }
    finally { setAskBusy(false); }
  };

  if (!state) return <AppShell><Intro onStarted={startNew} /></AppShell>;

  const total = state.sections.length;
  const done = state.sections.filter((s) => s.status === "complete").length;
  const pct = total ? Math.round(100 * done / total) : 0;

  return (
    <AppShell>
      <div className="py-10" data-testid="fafsa-root">
        <MissionEyebrow testId="fafsa-mission" />
        <div className="flex items-baseline justify-between mb-2">
          <h1 className="font-heading italic text-4xl sm:text-5xl text-[#0f172a]">FAFSA Walkthrough</h1>
          <button onClick={reset} data-testid="fafsa-reset-btn"
            className="text-xs text-slate-400 hover:text-red-600">Reset</button>
        </div>
        <p className="text-slate-600 mb-8">{done} of {total} sections complete · {pct}%</p>

        <div className="grid lg:grid-cols-[260px_1fr] gap-8">
          <aside className="space-y-2" data-testid="fafsa-sidebar">
            {state.sections.map((s) => {
              const isActive = activeKey === s.key;
              const complete = s.status === "complete";
              return (
                <button key={s.key} onClick={() => startSection(s.key)} data-testid={`fafsa-section-${s.key}`}
                  className={`w-full text-left rounded-xl border px-4 py-3 transition flex items-center justify-between gap-3 ${
                    isActive ? "border-[#1d4ed8] bg-[#eff6ff] text-[#0f172a]" :
                    complete ? "border-emerald-200 bg-emerald-50/50 text-[#0f172a]" :
                    "border-[#bfdbfe] bg-white text-[#0f172a] hover:border-[#1d4ed8]"
                  }`}>
                  <span className="text-sm font-medium">{SECTION_TITLES[s.key] || s.key}</span>
                  <span className={`text-xs ${complete ? "text-emerald-600" : "text-slate-400"}`}>
                    {complete ? "✓" : s.status === "in_progress" ? "•" : "○"}
                  </span>
                </button>
              );
            })}
          </aside>

          <section className="aw-card bg-white border border-[#bfdbfe] rounded-2xl p-6 sm:p-8 min-h-[400px]" data-testid="fafsa-lesson">
            {lessonLoading ? (
              <Spinner variant="block" label="Writing this lesson for your situation." testId="fafsa-lesson-loading" />
            ) : lesson?.celebration ? (
              <Celebration />
            ) : !activeKey ? (
              <p className="text-slate-600">Pick a section on the left to start.</p>
            ) : lesson?.error ? (
              <div className="text-red-700 text-sm">{lesson.error}</div>
            ) : !lesson ? (
              <Spinner variant="block" label="Loading" testId="fafsa-lesson-fetching" />
            ) : (
              <Lesson title={SECTION_TITLES[activeKey]} lesson={lesson}
                onComplete={completeSection}
                question={question} setQuestion={setQuestion}
                ask={ask} askBusy={askBusy} answer={answer}/>
            )}
          </section>
        </div>
      </div>
    </AppShell>
  );
}

function Lesson({ title, lesson, onComplete, question, setQuestion, ask, askBusy, answer }) {
  return (
    <div className="space-y-6" data-testid="fafsa-lesson-content">
      <div>
        <div className="text-xs uppercase tracking-widest text-[#1d4ed8] mb-2">FAFSA section</div>
        <h2 className="font-heading italic text-3xl text-[#0f172a]">{title}</h2>
      </div>
      {lesson.explanation && <p className="text-[#0f172a] leading-relaxed">{lesson.explanation}</p>}
      {Array.isArray(lesson.documents) && lesson.documents.length > 0 && (
        <div>
          <div className="text-sm font-medium text-[#0f172a] mb-2">Have ready</div>
          <ul className="space-y-1.5 text-sm text-slate-700 list-disc pl-5">
            {lesson.documents.map((d, i) => <li key={i}>{d}</li>)}
          </ul>
        </div>
      )}
      {lesson.worked_example && (
        <div className="rounded-xl bg-[#eff6ff] border border-[#bfdbfe] p-4">
          <div className="text-xs uppercase tracking-widest text-[#1d4ed8] mb-2">Worked example</div>
          <p className="text-sm text-[#0f172a] leading-relaxed">{lesson.worked_example}</p>
        </div>
      )}
      {Array.isArray(lesson.common_mistakes) && lesson.common_mistakes.length > 0 && (
        <div>
          <div className="text-sm font-medium text-[#0f172a] mb-2">Top 3 mistakes on this section</div>
          <ol className="space-y-1.5 text-sm text-slate-700 list-decimal pl-5">
            {lesson.common_mistakes.map((m, i) => <li key={i}>{m}</li>)}
          </ol>
        </div>
      )}
      {lesson.encouragement && <p className="text-sm italic text-emerald-700">{lesson.encouragement}</p>}

      <div className="border-t border-[#e5e7eb] pt-6">
        <div className="text-sm font-medium text-[#0f172a] mb-2">Have a question on this section?</div>
        <form onSubmit={ask} className="flex gap-2" data-testid="fafsa-ask-form">
          <input type="text" value={question} onChange={(e) => setQuestion(e.target.value)} data-testid="fafsa-ask-input"
            placeholder="e.g. My parents file separately — whose info do I use?"
            className="flex-1 rounded-xl border border-[#bfdbfe] px-4 py-2.5 text-sm focus:outline-none focus:border-[#1d4ed8]"/>
          <button type="submit" disabled={askBusy || question.trim().length < 3} data-testid="fafsa-ask-btn"
            className="rounded-full bg-[#0f172a] hover:bg-[#1e293b] text-white text-sm px-5 py-2.5 disabled:opacity-50">
            {askBusy ? "Thinking…" : "Ask"}
          </button>
        </form>
        {answer && (
          <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm text-[#0f172a] whitespace-pre-wrap leading-relaxed" data-testid="fafsa-ask-answer">
            {answer}
          </div>
        )}
      </div>
      <div className="border-t border-[#e5e7eb] pt-6 flex items-center justify-end">
        <button onClick={onComplete} data-testid="fafsa-complete-section-btn"
          className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af] text-white text-sm font-medium px-6 py-2.5">
          I'm ready — mark complete
        </button>
      </div>
    </div>
  );
}

function Celebration() {
  return (
    <div className="text-center py-10" data-testid="fafsa-celebration">
      <div className="font-heading italic text-4xl text-[#0f172a] mb-3">You're ready to submit your FAFSA</div>
      <p className="text-slate-600 max-w-md mx-auto">The hardest part is behind you. Head to the Calendar to set a submission goal date.</p>
      <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs px-4 py-2" data-testid="fafsa-ready-confetti">
        FAFSA Ready
      </div>
    </div>
  );
}

function Intro({ onStarted }) {
  const [firstTime, setFirstTime] = useState(true);
  const [dependent, setDependent] = useState("dependent");
  const [marital, setMarital] = useState("married");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await api.get(`/aw/fafsa/sections?dependent=${dependent}&first_time=${firstTime}`);
      onStarted({ first_time: firstTime, dependent, parent_marital: dependent === "independent" ? null : marital }, r.data?.sections || []);
    } finally { setBusy(false); }
  };

  return (
    <div className="py-10 max-w-2xl mx-auto" data-testid="fafsa-intro">
      <MissionEyebrow testId="fafsa-intro-mission" />
      <h1 className="font-heading italic text-4xl sm:text-5xl text-[#0f172a] mb-2">Walk me through the FAFSA</h1>
      <p className="text-slate-600 mb-8">Three quick questions so we can adapt the entire walkthrough to you.</p>
      <form onSubmit={submit} className="space-y-6 aw-card bg-white border border-[#bfdbfe] rounded-2xl p-6 sm:p-8" data-testid="fafsa-intro-form">
        <div>
          <div className="text-sm font-medium text-[#0f172a] mb-2">First time filing or returning?</div>
          <div className="flex gap-2">
            {[{v:true,label:"First time"},{v:false,label:"Returning filer"}].map((o) => (
              <button type="button" key={String(o.v)} onClick={() => setFirstTime(o.v)} data-testid={`fafsa-firsttime-${o.v}`}
                className={`rounded-full border px-4 py-2 text-sm transition ${
                  firstTime === o.v ? "border-[#1d4ed8] bg-[#eff6ff] text-[#0f172a]" :
                  "border-[#bfdbfe] bg-white text-slate-600 hover:border-[#1d4ed8]"}`}>
                {o.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-sm font-medium text-[#0f172a] mb-2">Dependent or independent student?</div>
          <div className="flex gap-2">
            {[{v:"dependent",label:"Dependent"},{v:"independent",label:"Independent"}].map((o) => (
              <button type="button" key={o.v} onClick={() => setDependent(o.v)} data-testid={`fafsa-dependent-${o.v}`}
                className={`rounded-full border px-4 py-2 text-sm transition ${
                  dependent === o.v ? "border-[#1d4ed8] bg-[#eff6ff] text-[#0f172a]" :
                  "border-[#bfdbfe] bg-white text-slate-600 hover:border-[#1d4ed8]"}`}>
                {o.label}
              </button>
            ))}
          </div>
        </div>
        {dependent !== "independent" && (
          <div>
            <div className="text-sm font-medium text-[#0f172a] mb-2">Parent marital status</div>
            <select value={marital} onChange={(e) => setMarital(e.target.value)} data-testid="fafsa-marital"
              className="w-full rounded-xl border border-[#bfdbfe] px-4 py-2.5 bg-white text-sm">
              <option value="married">Married</option>
              <option value="divorced">Divorced</option>
              <option value="separated">Separated</option>
              <option value="widowed">Widowed</option>
              <option value="never_married">Never married</option>
              <option value="remarried">Remarried</option>
              <option value="unknown">Not sure / other</option>
            </select>
          </div>
        )}
        <button type="submit" disabled={busy} data-testid="fafsa-intro-submit"
          className="w-full rounded-full bg-[#1d4ed8] hover:bg-[#1e40af] text-white text-sm font-medium px-5 py-3 disabled:opacity-60">
          {busy ? "Setting up…" : "Start my walkthrough"}
        </button>
      </form>
    </div>
  );
}
